import ActionIconButton from '@components/Common/ActionIconButton';
import CRMStatusChip from '@components/Common/CRMStatusChip';
import CRMTabs from '@components/Common/CRMTabs';
import EmptyState from '@components/Common/EmptyState';
import RecordLink from '@components/Common/RecordLink';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import MintlyTable from '@components/Common/Table';
import type { ColumnsConfig } from '@components/Common/Table/cardFields';
import { adminAmbassadorInvitesQuery, adminAmbassadorPeopleQuery } from '@constants/graphQLQueries';
import { formatDate } from '@helpers/date';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import { humanizeEnum } from '@helpers/labels';
import { fmtMoney } from '@helpers/money';
import PriceCheckOutlinedIcon from '@mui/icons-material/PriceCheckOutlined';
import { Box } from '@mui/material';
import React, { useEffect, useState } from 'react';

import { PERSON_STATUS_LABELS, REWARD_STATUS_LABELS, VERDICT_TONE, verdictLabel } from '../Referrals/review';
import {
  INVITE_STATUS_TONE,
  PEOPLE_TABS,
  REWARD_STATUS_TONE,
  rewardTitleOf,
  STAGE_TONE,
  type Invite,
  type PeopleTab,
  type ReferredPage,
  type ReferredRow,
  type RewardRow,
} from './ambassador';

const PAGE_SIZE = 25;

/** MintlyTable's row renderers are typed loosely; each panel types its own rows. */
type Cells<T> = Record<string, (row: T) => React.ReactNode>;
const cells = <T,>(c: Cells<T>) => c as Record<string, (row: any, index: number) => React.ReactNode>;

/* Short on purpose: it sits under a name in a narrow column. The tooltip says what it means. */
const priorityChip = (
  <span title="Priority role: counts twice toward milestones">
    <CRMStatusChip status="open" label="Priority" />
  </span>
);

/* ── Candidates ─────────────────────────────────────────────────────────────── */

/**
 * The people an ambassador referred, by tab, a page at a time. Names arrive as first name and an
 * initial, and with no contact details: the backend shows a Super Admin what the ambassador sees.
 */
export const CandidatesPanel: React.FC<{ ambassadorId: string }> = ({ ambassadorId }) => {
  const [tab, setTab] = useState<PeopleTab>('all');
  const [page, setPage] = useState(0);
  const [data, setData] = useState<ReferredPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(false);
    fetchGraphQLApiData(adminAmbassadorPeopleQuery(ambassadorId, tab, page, PAGE_SIZE))
      .then((r) => live && setData(r?.data?.adminAmbassadorPeople ?? null))
      .catch(() => live && setError(true))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [ambassadorId, tab, page]);

  const tabIndex = PEOPLE_TABS.findIndex((t) => t.id === tab);
  const count = (id: PeopleTab) => (data ? Number(data[id] ?? 0) : null);
  const rows = (data?.items ?? []).filter(Boolean);

  /*
   * Three columns, not five. The workspace's centre column is about 480px at 1280 with the activity
   * rail open, and the table truncates rather than wraps, so role and city ride under the name and
   * the plain-words progress rides under the stage chip.
   */
  const body: Cells<ReferredRow> = {
    candidate: (p) => {
      const where = [p.role, p.city].filter(Boolean).join(', ');
      return (
        <Box className="amb-name">
          <span>{p.name || 'Unnamed'}</span>
          {where && <span className="amb-muted" title={where}>{where}</span>}
          {p.priority && priorityChip}
        </Box>
      );
    },
    stage: (p) => {
      const progress = p.statusReason || (p.status ? PERSON_STATUS_LABELS[p.status] ?? humanizeEnum(p.status) : '');
      return (
        <Box className="amb-name">
          {p.stage ? <span><CRMStatusChip status={STAGE_TONE[p.stage] ?? 'inactive'} label={humanizeEnum(p.stage)} /></span> : null}
          {progress && <span className="amb-muted" title={progress}>{progress}</span>}
        </Box>
      );
    },
    joined: (p) => formatDate(p.joinedAt),
  };
  const columns: ColumnsConfig = {
    candidate: { card: 'title', tablet: true },
    stage: { card: 'status', tablet: true },
    joined: { card: 'fact', tablet: true },
  };

  return (
    <Box className="amb-wide">
      <CRMTabs
        variant="inline"
        value={tabIndex < 0 ? 0 : tabIndex}
        onChange={(i) => {
          setTab(PEOPLE_TABS[i].id);
          setPage(0);
        }}
        tabs={PEOPLE_TABS.map((t) => ({ label: count(t.id) === null ? t.label : `${t.label} (${count(t.id)})` }))}
      />
      {error ? (
        <EmptyState compact title="Candidates could not be loaded" body="Try again in a moment." />
      ) : loading && !data ? (
        <SkeletonLoader rows={5} columns={3} />
      ) : rows.length === 0 ? (
        <EmptyState compact title="Nobody here yet" body="People this ambassador referred appear here once they sign up." />
      ) : (
        <MintlyTable
          rows={rows}
          getRowKey={(p: ReferredRow) => `${p.name}-${p.joinedAt}`}
          selectors={['candidate', 'stage', 'joined']}
          columns={columns}
          tableHeadRow={{ candidate: 'Candidate', stage: 'Stage', joined: 'Joined' }}
          tableBodyRow={cells(body)}
          pagination={{
            enabled: true,
            page,
            rowsPerPage: PAGE_SIZE,
            totalNumberOfRows: Number(data?.total ?? 0),
            handleChangePage: (_e, n) => setPage(n),
          }}
        />
      )}
    </Box>
  );
};

/* ── Invites ────────────────────────────────────────────────────────────────── */

/** Invites the ambassador emailed, newest first, with how well the attached CV fits our industries. */
export const InvitesPanel: React.FC<{ ambassadorId: string }> = ({ ambassadorId }) => {
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<Invite[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(false);
    fetchGraphQLApiData(adminAmbassadorInvitesQuery(ambassadorId, '', page, PAGE_SIZE))
      .then((r) => {
        if (!live) return;
        const data = r?.data?.adminAmbassadorInvites;
        setRows((data?.items ?? []).filter(Boolean));
        setTotal(Number(data?.total ?? 0));
      })
      .catch(() => live && setError(true))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [ambassadorId, page]);

  if (error) return <Box className="amb-wide"><EmptyState compact title="Invites could not be loaded" body="Try again in a moment." /></Box>;
  if (loading && rows.length === 0) return <Box className="amb-wide"><SkeletonLoader rows={5} columns={4} /></Box>;
  if (rows.length === 0) {
    return (
      <Box className="amb-wide">
        <EmptyState compact title="No invites sent" body="Invites this ambassador emails to candidates appear here." />
      </Box>
    );
  }

  const body: Cells<Invite> = {
    invitee: (i) => (
      <Box className="amb-name">
        <span>{i.name || 'Unnamed'}</span>
        {i.email && <span className="amb-muted">{i.email}</span>}
      </Box>
    ),
    relevance: (i) =>
      i.relevance ? (
        <CRMStatusChip status={VERDICT_TONE[i.relevance] ?? 'pending'} label={verdictLabel(i.relevance)} />
      ) : (
        <span className="amb-muted">{i.hasResume ? 'Not scored' : 'No CV'}</span>
      ),
    status: (i) => (i.status ? <CRMStatusChip status={INVITE_STATUS_TONE[i.status] ?? 'inactive'} label={humanizeEnum(i.status)} /> : '-'),
    sent: (i) => {
      const after = i.joinedAt
        ? `Joined ${formatDate(i.joinedAt)}`
        : (i.resendCount ?? 0) > 0
          ? `Resent ${i.resendCount} ${i.resendCount === 1 ? 'time' : 'times'}, last ${formatDate(i.lastSentAt)}`
          : '';
      return (
        <Box className="amb-name">
          <span>{formatDate(i.sentAt)}</span>
          {after && <span className="amb-muted" title={after}>{after}</span>}
        </Box>
      );
    },
  };
  const columns: ColumnsConfig = {
    invitee: { card: 'title', tablet: true },
    status: { card: 'status', tablet: true },
    relevance: { card: 'fact', tablet: true },
    sent: { card: 'fact' },
  };

  return (
    <Box className="amb-wide">
      <MintlyTable
        rows={rows}
        getRowKey={(i: Invite) => i._id}
        selectors={['invitee', 'relevance', 'status', 'sent']}
        columns={columns}
        tableHeadRow={{ invitee: 'Invitee', relevance: 'CV fit', status: 'Status', sent: 'Sent' }}
        tableBodyRow={cells(body)}
        pagination={{
          enabled: true,
          page,
          rowsPerPage: PAGE_SIZE,
          totalNumberOfRows: total,
          handleChangePage: (_e, n) => setPage(n),
        }}
      />
    </Box>
  );
};

/* ── Rewards ────────────────────────────────────────────────────────────────── */

/**
 * Where a reward under review is decided: Referrals, Payouts, on its Review view (the default),
 * narrowed to this ambassador. Approving stays there, with the evidence beside it, rather than
 * being a bare button here.
 */
export const reviewQueuePath = (ambassadorId: string) =>
  `/referrals?filter_payout_referrer=AMBASSADOR&filter_payout_referrer_id=${encodeURIComponent(ambassadorId)}`;

/**
 * Every reward, as the ambassador sees them. An approved reward can be marked paid here (`onPay`),
 * since paying one ambassador is often what brought someone to this page; one under review links
 * to the review queue.
 */
export const RewardsTable: React.FC<{ rows: RewardRow[]; ambassadorId: string; onPay?: (row: RewardRow) => void }> = ({
  rows,
  ambassadorId,
  onPay,
}) => {
  if (rows.length === 0) {
    return (
      <Box className="amb-wide">
        <EmptyState compact title="No rewards yet" body="A reward is created when a milestone is reached or a candidate is placed." />
      </Box>
    );
  }
  /* Amount under the title, for the same reason as the candidates table; the action is its own
     narrow column so it sits in the card footer on a phone. */
  const body: Cells<RewardRow> = {
    reward: (r) => (
      <Box className="amb-name">
        <span title={rewardTitleOf(r)}>{rewardTitleOf(r)}</span>
        <span className="amb-code">{fmtMoney(r.amount, r.currency)}</span>
        {r.priority && priorityChip}
      </Box>
    ),
    status: (r) => (
      <Box className="amb-name">
        {r.status ? (
          <span>
            <CRMStatusChip status={REWARD_STATUS_TONE[r.status] ?? 'inactive'} label={REWARD_STATUS_LABELS[r.status] ?? humanizeEnum(r.status)} />
          </span>
        ) : '-'}
        {r.rejectedReason && <span className="amb-muted" title={r.rejectedReason}>{r.rejectedReason}</span>}
      </Box>
    ),
    dates: (r) => (
      <Box className="amb-name">
        <span>{`Earned ${formatDate(r.earnedAt)}`}</span>
        {r.paidAt && <span className="amb-muted">{`Paid ${formatDate(r.paidAt)}`}</span>}
        {r.status === 'PAID' && (
          <span className="amb-muted" title={r.paymentRef ?? undefined}>
            {'Ref '}
            <span className="amb-code">{r.paymentRef || 'not recorded'}</span>
          </span>
        )}
      </Box>
    ),
    action: (r) =>
      r.status === 'EARNED' && onPay ? (
        <ActionIconButton label="Mark paid" icon={<PriceCheckOutlinedIcon fontSize="small" />} color="primary" size="small" onClick={() => onPay(r)} />
      ) : r.status === 'PENDING_REVIEW' ? (
        <RecordLink to={reviewQueuePath(ambassadorId)}>Review</RecordLink>
      ) : null,
  };
  const columns: ColumnsConfig = {
    reward: { card: 'title', tablet: true },
    status: { card: 'status', tablet: true },
    dates: { card: 'fact', tablet: true },
    action: { card: 'action', tablet: true },
  };
  return (
    <Box className="amb-wide amb-rewards">
      <MintlyTable
        rows={rows}
        getRowKey={(r: RewardRow) => r.id}
        selectors={['reward', 'status', 'dates', 'action']}
        columns={columns}
        tableHeadRow={{ reward: 'Reward', status: 'Status', dates: 'Dates', action: '' }}
        tableBodyRow={cells(body)}
        pagination={{ enabled: false, page: 0, rowsPerPage: rows.length, totalNumberOfRows: rows.length, handleChangePage: () => {} }}
      />
    </Box>
  );
};
